import { draftContentFragment } from '.';
import noop from 'lodash/noop';
import React from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Heading, Stack } from '@eluve/components';
import {
  Block,
  DynamicArtifactTemplate,
  dynamicArtifactTemplateSchema,
  flattenBlocks,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { SummarySectionMarkdown } from '@eluve/smart-blocks';
import {
  AppointmentSummaryKeys,
  summaryKeyToSectionTitleMap,
} from '@eluve/utils';

import { DiffExportButton } from './DiffExportButton';
import { DiffViewer } from './DiffViewer';
import { normalizeForDiff } from './normalizeForDiff';

export const SummaryDiffContent: React.FC = () => {
  const { appointmentId, tenantId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };
  const appointment = useCompleteFragment({
    fragment: draftContentFragment,
    key: { id: appointmentId },
  });

  const humanSoapNote = appointment?.humanOutputs?.[0];
  const humanSoapNoteContent = humanSoapNote?.output?.content;
  const { additionalNotes } = appointment?.doctor_interaction ?? {};

  const llmSoapNoteContent = humanSoapNote?.output?.llm_output?.content;

  const outputVariant =
    humanSoapNote?.output?.llm_output?.metadata?.output_variant;

  let hydratedLlmSummary: DynamicArtifactTemplate | undefined = undefined;
  let hydratedHumanOutput: DynamicArtifactTemplate | undefined = undefined;
  let llmBlocks: Block[] | undefined;
  let humanBlocks: Block[] | undefined;
  if (outputVariant) {
    const artifactTemplate = dynamicArtifactTemplateSchema.parse(
      outputVariant.template,
    );
    hydratedLlmSummary = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      llmSoapNoteContent ?? {},
    );
    hydratedHumanOutput = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      humanSoapNoteContent ?? {},
    );
    llmBlocks = flattenBlocks(hydratedLlmSummary.blocks);
    humanBlocks = flattenBlocks(hydratedHumanOutput.blocks);
  }

  return (
    <Stack gap={2}>
      <Stack
        className="mt-4"
        justify="between"
        align="center"
        gap={2}
        direction="horizontal"
      >
        <Heading>Summary</Heading>
        <DiffExportButton
          appointmentId={appointmentId}
          tenantId={tenantId}
          type="summary"
        />
      </Stack>
      {outputVariant ? (
        <Stack gap={8}>
          {(() => {
            if (!llmBlocks || !humanBlocks) {
              return null;
            }
            return llmBlocks.map((llmBlock) => {
              const matchingHumanBlock = humanBlocks.find(
                (b) => b.key === llmBlock.key,
              );
              return (
                <Stack key={llmBlock.label} align="stretch" gap={4}>
                  <Heading size="3xl">{llmBlock.label}</Heading>
                  <DiffViewer
                    oldBlock={llmBlock}
                    newBlock={matchingHumanBlock!}
                  />
                </Stack>
              );
            });
          })()}
        </Stack>
      ) : (
        <Stack gap={8}>
          {Object.entries(summaryKeyToSectionTitleMap).map(
            ([key, sectionTitle]) => {
              const summaryKey = key as AppointmentSummaryKeys;
              return (
                <Stack key={summaryKey} align="stretch" gap={4}>
                  <Heading size="lg" weight="semibold">
                    {sectionTitle}
                  </Heading>
                  <ReactDiffViewer
                    oldValue={normalizeForDiff(
                      llmSoapNoteContent?.[summaryKey],
                    )}
                    newValue={normalizeForDiff(
                      humanSoapNoteContent?.[summaryKey],
                    )}
                    splitView={true}
                    showDiffOnly={true}
                  />
                </Stack>
              );
            },
          )}
        </Stack>
      )}
      {additionalNotes && (
        <SummarySectionMarkdown
          key="AdditionalNotes"
          // TODO(jesse): this should get cleaned up and use a different component [ELU-949]
          summaryKey={`AdditionalNotes` as any}
          sectionTitle="Additional Notes"
          content={additionalNotes}
          disabled={true}
          handleContentChange={noop}
        />
      )}
    </Stack>
  );
};
